import { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import {
  Checkbox,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import _ from 'lodash'
import moment from 'moment'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import CardGraphicAndNameCell from 'src/components/DataGrid/cells/CardGraphicAndNameCell/CardGraphicAndNameCell'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import PillCell from 'src/components/DataGrid/cells/PillCell/PillCell'
import UserAvatarCell from 'src/components/DataGrid/cells/UserAvatarCell/UserAvatarCell'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import UtilityToolbar from 'src/components/DataGrid/UtilityToolbar/UtilityToolbar'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import RadioButton from 'src/components/RadioButton/RadioButton'
import {
  VirtualCardDeactivatingInIncrements,
  VirtualCardTypes,
} from 'src/enums'
import Crncy from 'src/localization/Crncy'
import Trns, {
  translateSingleString,
  useTranslate,
} from 'src/localization/Trns'
import {
  VirtualCardListModel,
  VirtualCardStatus,
} from 'src/models/virtualCard.models'
import { connectedPush } from 'src/navigation/Lnk'
import { myWalletVirtualCardDetailUrl } from 'src/navigation/urls'
import { useQueryOrganizationVirtualCardTypes } from 'src/queries/organizations/virtualCardTypes/virtualCardTypes.queries'
import { useQueryVirtualCardsPaginated } from 'src/queries/virtualCards.queries'
import { postReportVirtualCardsDownload } from 'src/redux/actions/data/reportDownloads.actions'
import {
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectReportDownloadLoadingFromState } from 'src/redux/selectors/data/reportDownloads.selectors'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'
import variables from 'src/style/variables.json'
import { convertEnumToSentenceCase } from 'src/utils/string.utils'
import { css } from 'styled-components'

type MyWalletVirtualCardsFilters = {
  search: string
  recipients: string[]
  sortBy?: string
  sortDescending?: boolean
  statuses?: VirtualCardStatus[]
  cardTypes?: VirtualCardTypes[]
  deactivatingIn?: string
}

type MyWalletVirtualCardsTableProps = {}

const MyWalletVirtualCardsTable: React.FC<
  MyWalletVirtualCardsTableProps
> = () => {
  const reportDownloadLoading = useSelector(
    selectReportDownloadLoadingFromState,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)
  const user = useSelector(selectUserListModelFromState)
  const history = useHistory()
  const dispatch = useDispatch()
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()

  const { recurringVirtualCardsEnabled } = useSelector(
    selectBrandFeaturesFromState,
  )

  const enableServerCardTypes =
    useSelector(selectFeatureFlaggerFromState)?.enable_server_card_types ??
    false

  const { data: virtualCardTypes } = useQueryOrganizationVirtualCardTypes({
    orgId: user?.organizationId,
    enabled: enableServerCardTypes,
  })

  // When server card types is disabled, fall back to pre-API behavior
  const showStandard = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.standard)
    : true
  const showRecurring = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.recurring) &&
      recurringVirtualCardsEnabled
    : recurringVirtualCardsEnabled
  const showGuestCard = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.guestAccess)
    : true
  const showGuestRecurring = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.recurringGuest) &&
      recurringVirtualCardsEnabled
    : recurringVirtualCardsEnabled
  const showDecliningBalance = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.decliningBalance)
    : false
  const showIssuerCycle = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.issuerCycle)
    : false

  const emptyFilterValues: MyWalletVirtualCardsFilters = {
    search: '',
    recipients: [],
    deactivatingIn: '',
    sortBy: 'updatedAt',
    sortDescending: true,
  }

  const [virtualCardsRows, setVirtualCardsRows] = useState<
    VirtualCardListModel[]
  >([])

  const [filters, setFilters] = useState<MyWalletVirtualCardsFilters>(() => {
    const searchParams = new URLSearchParams(window.location.search)
    return {
      search: searchParams.get('search') || '',
      statuses:
        (searchParams
          .get('statuses')
          ?.split(',')
          .filter(Boolean) as VirtualCardStatus[]) || [],
      recipients:
        searchParams.get('recipients')?.split(',').filter(Boolean) || [],
      deactivatingIn: searchParams.get('deactivatingIn') || '',
      sortBy: searchParams.get('sortBy') || 'updatedAt',
      sortDescending: searchParams.get('sortDescending') !== 'false',
    }
  })

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  })

  const updateUrl = useCallback(
    (newFilters: MyWalletVirtualCardsFilters) => {
      const params = new URLSearchParams()
      if (newFilters.search) params.set('search', newFilters.search)
      if (newFilters.statuses && newFilters.statuses.length) {
        newFilters.statuses.forEach((status) => {
          params.append('statuses', status)
        })
      }
      if (newFilters.recipients && newFilters.recipients.length) {
        newFilters.recipients.forEach((recipient) => {
          params.append('recipients', recipient)
        })
      }
      if (newFilters.cardTypes && newFilters.cardTypes.length) {
        newFilters.cardTypes.forEach((cardType) => {
          params.append('cardTypes', cardType)
        })
      }
      if (newFilters.deactivatingIn) {
        params.append('deactivatingIn', newFilters.deactivatingIn)
      }
      history.push({
        search: params.toString(),
      })
    },
    [history],
  )

  const handleFilterChange = useCallback(
    (newFilters: Partial<MyWalletVirtualCardsFilters>) => {
      const updatedFilters = { ...filters, ...newFilters }
      if (!_.isEqual(updatedFilters, filters)) {
        setFilters(updatedFilters)
      }
      _.debounce(updateUrl, 500)(updatedFilters)
    },
    [filters, updateUrl],
  )

  const handleResetFilters = useCallback(() => {
    setFilters(emptyFilterValues)
    updateUrl(emptyFilterValues)
  }, [updateUrl])

  const filtersFlyoutProps = {
    title: translate('Virtual Card Filters'),
    onReset: handleResetFilters,
    loading: reportDownloadLoading,
    filters: (
      <>
        {/* Card Status Section */}
        <div
          role="group"
          aria-labelledby="filter-card-status"
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
          `}
        >
          <div
            id="filter-card-status"
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            <Trns source={'Card Status'} />
          </div>
          <Checkbox
            label={<Trns source={'Active'} />}
            testId="activeChk"
            onClick={(): void => {
              const newStatuses = filters.statuses?.includes(
                VirtualCardStatus.Active,
              )
                ? filters.statuses?.filter(
                    (s) => s !== VirtualCardStatus.Active,
                  )
                : [...(filters.statuses || []), VirtualCardStatus.Active]
              handleFilterChange({ statuses: newStatuses })
            }}
            isSelected={
              !!(
                filters.statuses &&
                filters.statuses.includes(VirtualCardStatus.Active)
              )
            }
          />
          <Checkbox
            label={<Trns source={'Pending'} />}
            testId="pendingChk"
            onClick={(): void => {
              const newStatuses = filters.statuses?.includes(
                VirtualCardStatus.Pending,
              )
                ? filters.statuses?.filter(
                    (s) => s !== VirtualCardStatus.Pending,
                  )
                : [...(filters.statuses || []), VirtualCardStatus.Pending]
              handleFilterChange({ statuses: newStatuses })
            }}
            isSelected={
              !!(
                filters.statuses &&
                filters.statuses.includes(VirtualCardStatus.Pending)
              )
            }
          />
          <Checkbox
            label={<Trns source={'Inactive'} />}
            testId="inactiveChk"
            onClick={(): void => {
              const newStatuses = filters.statuses?.includes(
                VirtualCardStatus.Cancelled,
              )
                ? filters.statuses?.filter(
                    (s) => s !== VirtualCardStatus.Cancelled,
                  )
                : [...(filters.statuses || []), VirtualCardStatus.Cancelled]
              handleFilterChange({ statuses: newStatuses })
            }}
            isSelected={
              !!(
                filters.statuses &&
                filters.statuses.includes(VirtualCardStatus.Cancelled)
              )
            }
          />
          <Checkbox
            label={<Trns source={'Closed'} />}
            testId="closedChk"
            onClick={(): void => {
              const newStatuses = filters.statuses?.includes(
                VirtualCardStatus.Closed,
              )
                ? filters.statuses?.filter(
                    (s) => s !== VirtualCardStatus.Closed,
                  )
                : [...(filters.statuses || []), VirtualCardStatus.Closed]
              handleFilterChange({ statuses: newStatuses })
            }}
            isSelected={
              !!(
                filters.statuses &&
                filters.statuses.includes(VirtualCardStatus.Closed)
              )
            }
          />
        </div>

        <DividerLineDiv
          css={css`
            margin: 20px 0px;
          `}
        />

        {/* Card Type Section */}
        {(showStandard ||
          showRecurring ||
          showGuestCard ||
          showGuestRecurring ||
          showDecliningBalance ||
          showIssuerCycle) && (
          <>
            <div
              role="group"
              aria-labelledby="filter-card-type"
              css={css`
                display: flex;
                flex-direction: column;
                gap: 12px;
              `}
            >
              <div
                id="filter-card-type"
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                <Trns source={'Card Type'} />
              </div>
              {showStandard && (
                <Checkbox
                  label={<Trns source={'Standard'} />}
                  testId="standardChk"
                  onClick={(): void => {
                    const newCardTypes = filters.cardTypes?.includes(
                      VirtualCardTypes.standard,
                    )
                      ? filters.cardTypes?.filter(
                          (cardType) => cardType !== VirtualCardTypes.standard,
                        )
                      : [
                          ...(filters.cardTypes || []),
                          VirtualCardTypes.standard,
                        ]
                    handleFilterChange({ cardTypes: newCardTypes })
                  }}
                  isSelected={
                    !!(
                      filters.cardTypes &&
                      filters.cardTypes.includes(VirtualCardTypes.standard)
                    )
                  }
                />
              )}
              {showRecurring && (
                <Checkbox
                  label={<Trns source={'Recurring'} />}
                  testId="recurringChk"
                  onClick={(): void => {
                    const newCardTypes = filters.cardTypes?.includes(
                      VirtualCardTypes.recurring,
                    )
                      ? filters.cardTypes?.filter(
                          (cardType) => cardType !== VirtualCardTypes.recurring,
                        )
                      : [
                          ...(filters.cardTypes || []),
                          VirtualCardTypes.recurring,
                        ]
                    handleFilterChange({ cardTypes: newCardTypes })
                  }}
                  isSelected={
                    !!(
                      filters.cardTypes &&
                      filters.cardTypes.includes(VirtualCardTypes.recurring)
                    )
                  }
                />
              )}
              {showGuestCard && (
                <Checkbox
                  label={<Trns source={'Guest Card'} />}
                  testId="guestChk"
                  onClick={(): void => {
                    const newCardTypes = filters.cardTypes?.includes(
                      VirtualCardTypes.guestAccess,
                    )
                      ? filters.cardTypes?.filter(
                          (cardType) =>
                            cardType !== VirtualCardTypes.guestAccess,
                        )
                      : [
                          ...(filters.cardTypes || []),
                          VirtualCardTypes.guestAccess,
                        ]
                    handleFilterChange({ cardTypes: newCardTypes })
                  }}
                  isSelected={
                    !!(
                      filters.cardTypes &&
                      filters.cardTypes.includes(VirtualCardTypes.guestAccess)
                    )
                  }
                />
              )}
              {showGuestRecurring && (
                <Checkbox
                  label={<Trns source={'Guest Card Recurring'} />}
                  testId="guestRecurringChk"
                  onClick={(): void => {
                    const newCardTypes = filters.cardTypes?.includes(
                      VirtualCardTypes.recurringGuest,
                    )
                      ? filters.cardTypes?.filter(
                          (cardType) =>
                            cardType !== VirtualCardTypes.recurringGuest,
                        )
                      : [
                          ...(filters.cardTypes || []),
                          VirtualCardTypes.recurringGuest,
                        ]
                    handleFilterChange({ cardTypes: newCardTypes })
                  }}
                  isSelected={
                    !!(
                      filters.cardTypes &&
                      filters.cardTypes.includes(
                        VirtualCardTypes.recurringGuest,
                      )
                    )
                  }
                />
              )}
              {showDecliningBalance && (
                <Checkbox
                  label={<Trns source={'Declining Balance'} />}
                  testId="decliningBalanceChk"
                  onClick={(): void => {
                    const newCardTypes = filters.cardTypes?.includes(
                      VirtualCardTypes.decliningBalance,
                    )
                      ? filters.cardTypes?.filter(
                          (cardType) =>
                            cardType !== VirtualCardTypes.decliningBalance,
                        )
                      : [
                          ...(filters.cardTypes || []),
                          VirtualCardTypes.decliningBalance,
                        ]
                    handleFilterChange({ cardTypes: newCardTypes })
                  }}
                  isSelected={
                    !!(
                      filters.cardTypes &&
                      filters.cardTypes.includes(
                        VirtualCardTypes.decliningBalance,
                      )
                    )
                  }
                />
              )}
              {showIssuerCycle && (
                <Checkbox
                  label={translate('Recurring')}
                  testId="issuerCycleChk"
                  onClick={(): void => {
                    const newCardTypes = filters.cardTypes?.includes(
                      VirtualCardTypes.issuerCycle,
                    )
                      ? filters.cardTypes?.filter(
                          (cardType) =>
                            cardType !== VirtualCardTypes.issuerCycle,
                        )
                      : [
                          ...(filters.cardTypes || []),
                          VirtualCardTypes.issuerCycle,
                        ]
                    handleFilterChange({ cardTypes: newCardTypes })
                  }}
                  isSelected={
                    !!(
                      filters.cardTypes &&
                      filters.cardTypes.includes(VirtualCardTypes.issuerCycle)
                    )
                  }
                />
              )}
            </div>
            <DividerLineDiv
              css={css`
                margin: 20px 0px;
              `}
            />
          </>
        )}

        {/* Deactivating Time Section */}
        <div
          role="radiogroup"
          aria-labelledby="filter-deactivating-within"
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
          `}
        >
          <div
            id="filter-deactivating-within"
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            <Trns source={'Cards Deactivating Within'} />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <RadioButton
              onClick={(): void =>
                handleFilterChange({
                  deactivatingIn: VirtualCardDeactivatingInIncrements.in7Days,
                })
              }
              isSelected={
                filters.deactivatingIn ===
                VirtualCardDeactivatingInIncrements.in7Days
              }
              css={css`
                display: block;
              `}
              testId="7daysRadio"
              aria-label={translate('7 days')}
            />
            <div
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate('7 days')}
            </div>
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <RadioButton
              onClick={(): void =>
                handleFilterChange({
                  deactivatingIn: VirtualCardDeactivatingInIncrements.in14Days,
                })
              }
              isSelected={
                filters.deactivatingIn ===
                VirtualCardDeactivatingInIncrements.in14Days
              }
              css={css`
                display: block;
              `}
              testId="14daysRadio"
              aria-label={translate('14 days')}
            />
            <div
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate('14 days')}
            </div>
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <RadioButton
              onClick={(): void =>
                handleFilterChange({
                  deactivatingIn: VirtualCardDeactivatingInIncrements.in30Days,
                })
              }
              isSelected={
                filters.deactivatingIn ===
                VirtualCardDeactivatingInIncrements.in30Days
              }
              css={css`
                display: block;
              `}
              testId="30daysRadio"
              aria-label={translate('30 days')}
            />
            <div
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate('30 days')}
            </div>
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <RadioButton
              onClick={(): void => handleFilterChange({ deactivatingIn: '' })}
              isSelected={filters.deactivatingIn === ''}
              css={css`
                display: block;
              `}
              testId="anytimeRadio"
              aria-label={translate('Any time')}
            />
            <div
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate('Any time')}
            </div>
          </div>
        </div>

        <DividerLineDiv
          css={css`
            margin: 20px 0px;
          `}
        />

        {/* User Filter Section */}
        {/* TODO: delete this once we copy to virtual cards table */}
        {/* <div css={css`
          ${getFontCss(14, themeConstants.colors.font_primary, styleConstants.fontFamilies.medium)};
        `}>
          <Trns source={'Card User'} />
        </div>
        <UserDropdown
          css={css`margin-top: 8px;`}
          isCreatable={false}
          placeholder={translateSingleString(locale, 'All virtual card users')}
          // chooseOption={(val: string[]): void => handleFilterChange({ recipients: val })}
          chooseOption={(val: string): void => handleFilterChange({ recipients: [val] })}
          isMulti
          selectedValues={new Set(filters.recipients as string[])}
          optionGroups={[
            {
              options: [], // TODO: Add user options
            },
          ]}
          minimalDisplay
        /> */}
      </>
    ),
  }
  // virtual cards code
  const queryFilters = {
    search: filters.search !== '' ? filters.search : undefined,
    statuses:
      filters.statuses && filters.statuses.length > 0
        ? filters.statuses
        : undefined,
    recipient: user?.id ? user.id : undefined,
    cardTypes:
      filters.cardTypes && filters.cardTypes.length > 0
        ? filters.cardTypes
        : undefined,
    deactivatingIn:
      filters.deactivatingIn !== '' ? filters.deactivatingIn : undefined,
    sortField: filters.sortBy,
    sortDirection: filters.sortDescending ? 'DESC' : 'ASC',
    page: paginationModel.page,
    count: paginationModel.pageSize,
  }

  // ACTIVE COUNT QUERY
  const { data: totalActiveCountData, isLoading: isTotalActiveCountLoading } =
    useQueryVirtualCardsPaginated({
      filters: {
        ...queryFilters,
        statuses: [VirtualCardStatus.Active],
        count: 1,
        page: 0,
      },
    })

  const {
    data: virtualCardData,
    isPending,
    isSuccess: isVirtualCardsSuccess,
  } = useQueryVirtualCardsPaginated({
    filters: queryFilters,
  })

  const showLoadingState = isPending && isTotalActiveCountLoading

  useEffect(() => {
    if (
      virtualCardData &&
      virtualCardData.virtualCards &&
      isVirtualCardsSuccess
    ) {
      if (!_.isEqual(virtualCardData.virtualCards, virtualCardsRows)) {
        setVirtualCardsRows(virtualCardData.virtualCards)
      }
    }
  }, [isVirtualCardsSuccess, isPending, paginationModel, filters])

  const isDisabled = (status: VirtualCardStatus): boolean => {
    return (
      status === VirtualCardStatus.Cancelled ||
      status === VirtualCardStatus.Expired ||
      status === VirtualCardStatus.Rejected
    )
  }

  const columns: Array<GridColDef<VirtualCardListModel>> = [
    {
      field: 'cardName',
      headerName: translateSingleString(locale, 'Card Name'),
      width: 300,
      sortable: false,
      renderCell: (params): React.ReactElement => {
        return (
          <CardGraphicAndNameCell
            text={params.row.displayName}
            cardTypeImageSrc={params.row.cardTypeImageSrc}
            cardTypeAlt={params.row.issuer?.name ?? ''}
            disabled={isDisabled(params.row.status)}
            bold
            params={params}
            data-iss-id={params.row.issuer?.id}
          />
        )
      },
    },
    {
      field: 'cardCreator',
      headerName: translateSingleString(locale, 'Card Creator'),
      width: 200,
      sortable: false,
      renderCell: (params): React.ReactElement => {
        return (
          <UserAvatarCell
            user={
              params.row.cardholder ?? {
                id: '',
                fullNameOrEmail: '',
                initials: '',
                hasFullName: false,
              }
            }
            params={params}
          />
        )
      },
    },
    {
      field: 'cardType',
      headerName: translateSingleString(locale, 'Card Type'),
      width: 150,
      sortable: false,
      renderCell: (params): React.ReactElement => {
        return (
          <DataGridCell
            text={params.row.cardTypeLabel}
            params={params}
            data-card-type={params.row.cardTypeLabelSrc}
          />
        )
      },
    },
    {
      field: 'cardStatus',
      headerName: translateSingleString(locale, 'Status'),
      width: 150,
      sortable: false,
      renderCell: (params): React.ReactElement => {
        let tagColor: 'highlight' | 'severe' | 'pending' | undefined
        switch (params.row.status) {
          case VirtualCardStatus.Active:
            tagColor = 'highlight'
            break
          case VirtualCardStatus.Cancelled:
          case VirtualCardStatus.Rejected:
          case VirtualCardStatus.Closed:
            tagColor = 'severe'
            break
          case VirtualCardStatus.Pending:
            tagColor = 'pending'
            break
          case VirtualCardStatus.Expired:
          default:
            tagColor = undefined
        }
        const statusText = translateSingleString(
          locale,
          convertEnumToSentenceCase(params.row.status),
        )
        return (
          <PillCell
            color={tagColor}
            params={params}
            data-status={params.row.status}
          >
            {statusText}
          </PillCell>
        )
      },
    },
    {
      field: 'activeUntil',
      headerName: translateSingleString(locale, 'Active Until'),
      width: 200,
      sortable: false,
      renderCell: (params): React.ReactElement => {
        return (
          <DataGridCell
            text={moment(params.row.activeUntil).format('MMM D, YYYY')}
            params={params}
          />
        )
      },
    },
    {
      field: 'availableCredit',
      align: 'right',
      headerName: translateSingleString(locale, 'Available Credit'),
      width: 150,
      sortable: false,
      renderCell: (params): React.ReactElement => {
        return (
          <DataGridCell
            text={
              <Crncy
                currency={params.row.currency}
                amount={params.row.balance}
              />
            }
            params={params}
          />
        )
      },
    },
    chevronColumnDef,
  ]

  const NoResultsMessage = (): ReactElement => (
    <div
      css={css`
        padding-top: 40px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      `}
    >
      {translateSingleString(
        locale,
        'No results found. Please check your filters and try again.',
      )}
    </div>
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: ${variables.units['base-unit-3']}px;
        border: 1px solid ${styleConstants.colors.gray_medium};
        background-color: ${styleConstants.colors.white};
      `}
    >
      <UtilityToolbar
        header={
          <span
            css={css`
              ${getFontCss(
                variables.units['base-unit-5'],
                themeConstants.colors.highlight,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            <Trns
              source={'${count} Active'}
              expressions={{
                count: `${
                  typeof totalActiveCountData?.totalItems === 'number'
                    ? totalActiveCountData?.totalItems
                    : '--'
                }`,
              }}
            />
          </span>
        }
        searchProps={{
          value: filters.search,
          onChange: (val: string) => handleFilterChange({ search: val }),
          placeholder: translateSingleString(
            locale,
            'Name, Available Credit, Last 4, etc.',
          ),
        }}
        filtersFlyoutProps={filtersFlyoutProps}
        utilityToolbarDownloadProps={{
          onClick: (): void => {
            analytics?.track(
              'my wallet - virtual cards - download my virtual cards',
            )
            dispatch(postReportVirtualCardsDownload(filters))
          },
        }}
      />
      <DataGrid
        rows={
          isVirtualCardsSuccess && !showLoadingState ? virtualCardsRows : []
        }
        columns={columns}
        infiniteScrollProps={{
          loading: showLoadingState,
          complete: !showLoadingState,
          fetch: () => {}, // do nothing because no pagination
        }}
        filtersBody={{}}
        slots={{
          loadingOverlay: () => <LoadingBackgroundPage />,
          noResultsOverlay: () => <NoResultsMessage />,
          noRowsOverlay: () => <NoResultsMessage />,
        }}
        rowHeight={DataGridRowHeight.Comfy}
        rowCount={virtualCardData?.totalItems}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pagination
        paginationMode={'server'}
        pageSizeOptions={[]}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        onRowClick={(row): void => {
          const virtualCardId = row.id.toString()
          dispatch(
            connectedPush({
              pathname: myWalletVirtualCardDetailUrl(virtualCardId),
              search: '',
              hash: '',
              state: {},
            }),
          )
          analytics?.track('virtual cards - select virtual card - item press', {
            intent: 'select virtual card',
            virtualCardSelected: virtualCardId,
            interaction: 'item press',
          })
        }}
      />
    </div>
  )
}

export default MyWalletVirtualCardsTable
